import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, Trash2, Users, MapPin, GitMerge, Loader2 } from "lucide-react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

function initials(name = "") {
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase() || "NP";
}

// Normalize an NP name so "Kimberly McKenna" and "Kimberly McKenna, NP" collide
function _norm(name = "") {
  return name.toLowerCase()
    .replace(/^(dr|dr\.|doctor)\s+/i, "")
    .replace(/[,\s]+(np-?c?|md|do|pa-?c?|rn|fnp-?c?|arnp|dnp|phd)\.?$/i, "")
    .replace(/\s+/g, " ").trim();
}

const empty = { name: "", phone: "", email: "", address: "", city: "", state: "", zip: "", avatar_url: "", notes: "" };

export default function NPListPage() {
  const [nps, setNps] = useState([]);
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/nps"); setNps(data); }
    catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[nps] load failed:", e?.message || e);
      toast.error("Could not load");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const allStates = Array.from(new Set(nps.flatMap((n) => n.coverage_states || []))).sort();

  const filtered = nps.filter((n) => {
    if (stateFilter !== "all" && !(n.coverage_states || []).includes(stateFilter)) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return n.name?.toLowerCase().includes(s) || n.email?.toLowerCase().includes(s);
  });

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/nps", form); toast.success("NP added"); setOpen(false); setForm(empty); load(); }
    catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this NP?")) return;
    try { await api.delete(`/nps/${id}`); load(); } catch { toast.error("Delete failed"); }
  };

  // Find duplicate name groups
  const dupeGroups = (() => {
    const groups = {};
    nps.forEach(n => {
      const k = _norm(n.name);
      if (!k) return;
      (groups[k] = groups[k] || []).push(n);
    });
    return Object.values(groups).filter(g => g.length > 1);
  })();

  const [mergeOpen, setMergeOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const mergePair = async (primary, dupe) => {
    if (!window.confirm(`Merge "${dupe.name}" INTO "${primary.name}"? Every facility, visit, note, and credential attached to ${dupe.name} will be reassigned to ${primary.name}, and ${dupe.name} will be deleted.`)) return;
    setMerging(true);
    try {
      const r = await api.post("/nps/merge", { primary_id: primary.id, duplicate_id: dupe.id });
      const c = r.data?.reassigned || {};
      toast.success(`Merged into ${r.data.primary_name} · ${c.facilities || 0} fac · ${c.visits || 0} visits`, { duration: 10000 });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
    finally { setMerging(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A]">Team</div>
          <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2">Nurse Practitioners</h1>
          <p className="text-sm text-[#5C5850] mt-1.5">{nps.length} on staff.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="add-np-btn">
          <Plus className="h-4 w-4 mr-2" /> Add NP
        </Button>
      </div>

      {dupeGroups.length > 0 && (
        <div className="mt-5 bg-[#FEF7E6] border border-[#E5D9BE] rounded-md p-4" data-testid="dupe-nps-alert">
          <div className="flex items-start gap-3">
            <GitMerge className="h-5 w-5 text-[#8A6E2A] mt-0.5"/>
            <div className="flex-1">
              <div className="font-semibold text-[#8A6E2A]">
                {dupeGroups.length} possible duplicate NP{dupeGroups.length === 1 ? "" : "s"} detected
              </div>
              <div className="text-xs text-[#8A6E2A] mt-0.5">Names that normalize to the same value (e.g. "Kimberly McKenna" and "Kimberly McKenna, NP").</div>
              <button onClick={() => setMergeOpen(true)} className="mt-2 text-xs px-3 py-1.5 rounded bg-[#8A6E2A] text-white hover:bg-[#6E5820]" data-testid="review-dupes-btn">Review & merge</button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847A]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="pl-9 h-11 bg-white border-[#E5E0D8]" data-testid="np-search" />
      </div>

      {allStates.length > 0 && (
        <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="np-state-filter">
          <MapPin className="h-3.5 w-3.5 text-[#8A847A]"/>
          <button
            onClick={() => setStateFilter("all")}
            className={`px-2.5 py-1 rounded text-[11px] font-mono uppercase transition-colors ${stateFilter === "all" ? "bg-[#4A6B5D] text-white" : "bg-white border border-[#E5E0D8] text-[#5C5850] hover:bg-[#F0EBE1]"}`}
            data-testid="np-state-filter-all"
          >All ({nps.length})</button>
          {allStates.map((st) => {
            const count = nps.filter((n) => (n.coverage_states || []).includes(st)).length;
            return (
              <button
                key={st}
                onClick={() => setStateFilter(st)}
                className={`px-2.5 py-1 rounded text-[11px] font-mono uppercase transition-colors ${stateFilter === st ? "bg-[#4A6B5D] text-white" : "bg-white border border-[#E5E0D8] text-[#5C5850] hover:bg-[#F0EBE1]"}`}
                data-testid={`np-state-filter-${st}`}
              >{st} ({count})</button>
            );
          })}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.length === 0 && (
          <div className="col-span-full bg-white border border-[#E5E0D8] rounded-md p-10 text-center text-[#8A847A] text-sm">
            <Users className="h-8 w-8 mx-auto opacity-60 mb-2"/>
            No practitioners yet.
          </div>
        )}
        {filtered.map((n) => (
          <div key={n.id} className="bg-white border border-[#E5E0D8] rounded-md p-4" data-testid={`np-card-${n.id}`}>
            <div className="flex items-start justify-between">
              <Link to={`/nps/${n.id}`} className="flex items-center gap-3 min-w-0 flex-1">
                <Avatar className="h-11 w-11">
                  <AvatarImage src={n.avatar_url} alt={n.name} />
                  <AvatarFallback className="bg-[#F0EBE1] text-[#4A6B5D] font-semibold">{initials(n.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="font-medium text-[#292723] hover:text-[#4A6B5D] truncate">{n.name}</div>
                  <div className="text-xs text-[#8A847A] truncate">{n.email || n.phone || "—"}</div>
                </div>
              </Link>
              <Button variant="ghost" size="sm" onClick={() => remove(n.id)} className="text-[#C17D6E]" data-testid={`delete-np-${n.id}`}>
                <Trash2 className="h-4 w-4"/>
              </Button>
            </div>
            <div className="grid grid-cols-3 mt-4">
              <div className="col-span-2 border-r border-[#F0EBE1] pr-3">
                <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">Visits · last 7 days</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className={`h-2 w-2 rounded-full ${(n.visits_last_7d || 0) === 0 ? "bg-[#C17D6E]" : (n.visits_last_7d || 0) < 3 ? "bg-[#B08A55]" : "bg-[#4A6B5D]"}`} />
                  <span className="heading text-3xl font-bold text-[#292723] font-mono">{n.visits_last_7d ?? 0}</span>
                  <span className="text-xs text-[#8A847A]">· {n.visits_last_30d ?? 0} in 30d</span>
                </div>
              </div>
              <div className="pl-3 text-center">
                <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">Facilities</div>
                <div className="font-mono font-semibold text-[#292723] mt-1">{n.facility_count ?? 0}</div>
                <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A] mt-1">Patients</div>
                <div className="font-mono font-semibold text-[#292723]">{n.patient_count ?? 0}</div>
              </div>
            </div>
            {(n.coverage_states?.length > 0) && (
              <div className="mt-3 pt-3 border-t border-[#F0EBE1] flex items-center gap-2 flex-wrap" data-testid={`np-coverage-${n.id}`}>
                <MapPin className="h-3 w-3 text-[#8A847A] flex-shrink-0"/>
                {n.coverage_states.map((st) => (
                  <span key={st} className="text-[10px] font-mono font-semibold uppercase px-1.5 py-0.5 rounded bg-[#F0EBE1] text-[#4A6B5D]">
                    {st}
                  </span>
                ))}
                {n.coverage_cities?.length > 0 && (
                  <span className="text-[10px] text-[#8A847A] truncate ml-1">{n.coverage_cities.slice(0, 2).join(" · ")}{n.coverage_cities.length > 2 ? ` +${n.coverage_cities.length - 2}` : ""}</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <Sheet open={mergeOpen} onOpenChange={setMergeOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader><SheetTitle className="heading text-2xl">Review duplicates</SheetTitle></SheetHeader>
          <div className="mt-6 space-y-6">
            {dupeGroups.map((group, gi) => (
              <div key={gi} className="border border-[#E5E0D8] rounded-md p-4">
                <div className="text-xs uppercase tracking-[0.08em] text-[#5C5850] mb-2">Duplicate #{gi + 1}</div>
                <div className="space-y-3">
                  {group.map((n) => (
                    <div key={n.id} className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-[#292723]">{n.name}</div>
                        <div className="text-[11px] text-[#8A847A]">
                          {n.facility_count ?? 0} fac · {n.visits_last_30d ?? 0} visits (30d) · {(n.note_entries?.length) ?? 0} notes
                          {n.drchrono_doctor_id && <span className="ml-2 text-[#4A6B5D]">· DrChrono linked</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => mergePair(n, group.find(x => x.id !== n.id))}
                        disabled={merging}
                        className="text-[10px] px-2.5 py-1.5 rounded bg-[#4A6B5D] text-white hover:bg-[#3E594D] disabled:opacity-50"
                        data-testid={`keep-np-${n.id}`}
                      >
                        {merging ? <Loader2 className="h-3 w-3 animate-spin"/> : "Keep this one"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader><SheetTitle className="heading text-2xl">Add NP</SheetTitle></SheetHeader>
          <form onSubmit={submit} className="mt-6 space-y-4">
            {[["name","Name",true],["phone","Phone"],["email","Email"],["avatar_url","Avatar URL"]].map(([k, l, req]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">{l}</Label>
                <Input required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5 h-10 bg-white border-[#E5E0D8]" data-testid={`np-form-${k}`} />
              </div>
            ))}
            <div>
              <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Address</Label>
              <div className="mt-1.5">
                <AddressAutocomplete
                  value={form.address}
                  onChange={(v) => setForm({ ...form, address: v })}
                  onSelect={(s) => {
                    setForm((f) => ({
                      ...f,
                      address: s.street || s.label,
                      city: s.city || f.city,
                      state: s.state || f.state,
                      zip: s.postcode || f.zip,
                    }));
                  }}
                  testId="np-form-address"
                  placeholder="Start typing an address…"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[["city","City"],["state","State"],["zip","Zip"]].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">{l}</Label>
                  <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5 h-10 bg-white border-[#E5E0D8]" data-testid={`np-form-${k}`} />
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5 bg-white border-[#E5E0D8]" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="save-np-btn">Save NP</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
